import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";

import { usePageTitle } from "@/hooks/usePageTitle";
import {
  filterImportedIntegrators,
  formatImportedIntegratorSyncDate,
  importedIntegratorEnvironmentLabel,
  importedIntegratorOriginLabel,
  importedIntegratorSyncLabel,
  listAdminImportedIntegrators,
  summarizeImportedIntegrators,
  type AdminImportedIntegrator,
  type ImportedIntegratorFilter,
  type ImportedIntegratorSyncStatus,
} from "@/lib/api/admin-imported-integrators";
import { useIntegratorSyncEnabled } from "@/lib/feature-flags/useSandboxCredentialsEnabled";
import { AdminShell } from "@/pages/admin/AdminShell";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "error"; message: string };

const FILTERS: { id: ImportedIntegratorFilter; label: string; hint: string }[] =
  [
    { id: "all", label: "All", hint: "imported links" },
    { id: "sandbox", label: "Sandbox", hint: "sandbox environment" },
    { id: "live", label: "Live", hint: "live environment" },
    { id: "sync_failed", label: "Sync failed", hint: "needs a retry" },
  ];

function syncBadgeClass(status: ImportedIntegratorSyncStatus): string {
  if (status === "linked") return "s-approved";
  if (status === "failed") return "s-denied";
  return "s-review";
}

export default function AdminImportedIntegratorsPage() {
  usePageTitle("Imported integrators — API Admin Panel");
  const integratorSyncEnabled = useIntegratorSyncEnabled();
  const [rows, setRows] = useState<AdminImportedIntegrator[]>([]);
  const [filter, setFilter] = useState<ImportedIntegratorFilter>("all");
  const [loadState, setLoadState] = useState<LoadState>({ kind: "loading" });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!integratorSyncEnabled) {
        return;
      }
      setLoadState({ kind: "loading" });
      try {
        const response = await listAdminImportedIntegrators();
        if (cancelled) {
          return;
        }
        setRows(response.integrators);
        setLoadState({ kind: "ready" });
      } catch {
        if (cancelled) {
          return;
        }
        setRows([]);
        setLoadState({
          kind: "error",
          message: "Could not load imported integrators. Try again.",
        });
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [integratorSyncEnabled, reloadKey]);

  const summary = useMemo(() => summarizeImportedIntegrators(rows), [rows]);
  const visibleRows = useMemo(
    () => filterImportedIntegrators(rows, filter),
    [rows, filter],
  );

  if (!integratorSyncEnabled) {
    return <Navigate to="/admin/control-panel" replace />;
  }

  function countFor(id: ImportedIntegratorFilter): number {
    if (id === "sandbox") return summary.sandbox;
    if (id === "live") return summary.live;
    if (id === "sync_failed") return summary.syncFailed;
    return summary.total;
  }

  return (
    <AdminShell
      activeNav="imported-integrators"
      title="Imported integrators"
      description="Sandbox and live integrators already held in Glofox. Hand-submitted access requests stay on Access requests."
      testId="admin-imported-integrators-page"
    >
      <div
        className="sum-row"
        role="group"
        aria-label="Filter imported integrators"
        data-testid="admin-imported-integrators-filters"
      >
        {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`sum-card${filter === item.id ? " on" : ""}`}
            aria-pressed={filter === item.id}
            data-testid={`admin-imported-integrators-filter-${item.id}`}
            onClick={() => setFilter(item.id)}
          >
            <div className="sum-lbl">{item.label}</div>
            <div
              className={`sum-val${
                item.id === "sync_failed" ? " sum-val--denied" : ""
              }`}
              data-testid={`admin-imported-integrators-count-${item.id}`}
            >
              {countFor(item.id)}
            </div>
            <div className="sum-sub">{item.hint}</div>
          </button>
        ))}
      </div>

      <div
        className="mobile-status"
        role="group"
        aria-label="Filter imported integrators"
        data-testid="admin-imported-integrators-mobile-filters"
      >
        {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`mobile-status-btn${filter === item.id ? " on" : ""}`}
            aria-pressed={filter === item.id}
            data-testid={`admin-imported-integrators-mobile-filter-${item.id}`}
            onClick={() => setFilter(item.id)}
          >
            {item.label} <span>{countFor(item.id)}</span>
          </button>
        ))}
      </div>

      <div className="tbl-wrap">
        {loadState.kind === "loading" ? (
          <div className="empty-state" data-testid="admin-imported-integrators-loading">
            <i className="ti ti-loader-2" aria-hidden="true" />
            <p>Loading imported integrators…</p>
          </div>
        ) : null}

        {loadState.kind === "error" ? (
          <div className="empty-state" data-testid="admin-imported-integrators-error">
            <i className="ti ti-alert-circle" aria-hidden="true" />
            <p>{loadState.message}</p>
            <button
              type="button"
              className="filter-btn on"
              onClick={() => setReloadKey((value) => value + 1)}
            >
              Retry
            </button>
          </div>
        ) : null}

        {loadState.kind === "ready" && visibleRows.length === 0 ? (
          <div className="empty-state" data-testid="admin-imported-integrators-empty">
            <i className="ti ti-link" aria-hidden="true" />
            <p>
              {filter === "all"
                ? "No imported integrators yet."
                : "No imported integrators match this filter."}
            </p>
          </div>
        ) : null}

        {loadState.kind === "ready" && visibleRows.length > 0 ? (
          <div
            className="tbl-scroll"
            data-testid="admin-imported-integrators-list"
            role="region"
            tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex -- overflow lists must take keyboard focus
            aria-label="Imported integrators"
          >
            <table className="req-table imported-integrators-table">
              <thead>
                <tr>
                  <th scope="col">Applicant</th>
                  <th scope="col">Environment</th>
                  <th scope="col">Integrator ID</th>
                  <th scope="col">Origin</th>
                  <th scope="col">Sync</th>
                  <th scope="col">Last sync</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr
                    key={row.id}
                    className="req-tr req-tr--static"
                    data-testid={`admin-imported-integrator-row-${row.id}`}
                  >
                    <td>
                      <div className="req-name">{row.contact_name}</div>
                      <div className="req-email">{row.email}</div>
                      <div className="req-biz">{row.company_name}</div>
                    </td>
                    <td>{importedIntegratorEnvironmentLabel(row.key_environment)}</td>
                    <td>
                      <code
                        className="request-id-code"
                        data-testid={`admin-imported-integrator-id-${row.id}`}
                      >
                        {row.integrator_id}
                      </code>
                    </td>
                    <td>{importedIntegratorOriginLabel()}</td>
                    <td>
                      <span className={`status-badge ${syncBadgeClass(row.sync_status)}`}>
                        <span className="s-dot" />
                        {importedIntegratorSyncLabel(row.sync_status)}
                      </span>
                    </td>
                    <td>
                      <div className="req-date">
                        {formatImportedIntegratorSyncDate(row.last_sync_at)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </AdminShell>
  );
}
